import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Sun, Moon, Star, Calendar, Compass, Grid3X3, Activity, 
  Clock, ChevronRight, Download, Sparkles, Layers, Eye,
  BarChart3, CircleDot, Zap, BookOpen, RefreshCw
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'https://taraanaya-prod-production.up.railway.app';

// Planet color mapping
const planetColors = {
  Sun: { bg: 'from-orange-500/20 to-amber-500/20', text: 'text-orange-400', border: 'border-orange-500/30' },
  Moon: { bg: 'from-slate-400/20 to-blue-300/20', text: 'text-slate-300', border: 'border-slate-400/30' },
  Mars: { bg: 'from-red-500/20 to-rose-500/20', text: 'text-red-400', border: 'border-red-500/30' },
  Mercury: { bg: 'from-emerald-500/20 to-green-500/20', text: 'text-emerald-400', border: 'border-emerald-500/30' },
  Jupiter: { bg: 'from-yellow-500/20 to-amber-400/20', text: 'text-yellow-400', border: 'border-yellow-500/30' },
  Venus: { bg: 'from-pink-500/20 to-rose-400/20', text: 'text-pink-400', border: 'border-pink-500/30' },
  Saturn: { bg: 'from-indigo-500/20 to-blue-500/20', text: 'text-indigo-400', border: 'border-indigo-500/30' },
  Rahu: { bg: 'from-purple-500/20 to-violet-500/20', text: 'text-purple-400', border: 'border-purple-500/30' },
  Ketu: { bg: 'from-amber-600/20 to-orange-600/20', text: 'text-amber-500', border: 'border-amber-600/30' },
  Asc: { bg: 'from-cyan-500/20 to-teal-500/20', text: 'text-cyan-400', border: 'border-cyan-500/30' }
};

const getPlanetStyle = (planet) => planetColors[planet] || { bg: 'from-gray-500/20 to-slate-500/20', text: 'text-gray-400', border: 'border-gray-500/30' };

// Tab configuration
const tabs = [
  { id: 'overview', label: 'Overview', icon: Eye },
  { id: 'planets', label: 'Planets', icon: CircleDot },
  { id: 'vargas', label: 'Vargas', icon: Grid3X3 },
  { id: 'dasha', label: 'Dasha', icon: Clock },
  { id: 'varshaphala', label: 'Varshaphala', icon: Calendar },
  { id: 'shadbala', label: 'Shadbala', icon: BarChart3 },
  { id: 'ashtakavarga', label: 'Ashtakavarga', icon: Layers },
  { id: 'jaimini', label: 'Jaimini', icon: Star },
  { id: 'kp', label: 'KP System', icon: Compass },
  { id: 'upagrahas', label: 'Upagrahas', icon: Zap },
  { id: 'yogas', label: 'Yogas', icon: Sparkles },
];

// Loading Component
const LoadingScreen = () => (
  <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E1A' }}>
    <div className="text-center">
      <motion.div
        className="w-20 h-20 mx-auto mb-6 rounded-full"
        style={{ 
          background: 'linear-gradient(135deg, #22D3EE, #8B5CF6)',
          boxShadow: '0 0 60px rgba(34, 211, 238, 0.4)'
        }}
        animate={{ 
          scale: [1, 1.2, 1],
          rotate: [0, 180, 360]
        }}
        transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.p 
        className="text-xl font-light"
        style={{ 
          background: 'linear-gradient(90deg, #22D3EE, #A78BFA)',
          WebkitBackgroundClip: 'text',
          WebkitTextFillColor: 'transparent'
        }}
        animate={{ opacity: [0.5, 1, 0.5] }}
        transition={{ duration: 1.5, repeat: Infinity }}
      >
        Loading Astrological Kernel...
      </motion.p>
    </div>
  </div>
);

// Section Card Component
const SectionCard = ({ title, icon: Icon, children, className = '' }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={`rounded-2xl border backdrop-blur-xl ${className}`}
    style={{ 
      background: 'linear-gradient(135deg, rgba(15, 23, 42, 0.8), rgba(30, 41, 59, 0.6))',
      borderColor: 'rgba(99, 102, 241, 0.2)'
    }}
  >
    {title && (
      <div className="px-6 py-4 border-b border-white/10 flex items-center gap-3">
        {Icon && <Icon className="w-5 h-5 text-cyan-400" />}
        <h3 className="text-lg font-semibold text-white">{title}</h3>
      </div>
    )}
    <div className="p-6">{children}</div>
  </motion.div>
);

// Overview Tab
const OverviewTab = ({ data }) => {
  if (!data) return null;
  
  const { birth_particulars, astronomical_data, hindu_calendar, panchang, ascendant } = data;
  
  return (
    <div className="space-y-6">
      {/* Hero Card */}
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="rounded-3xl p-8 relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.15), rgba(139, 92, 246, 0.1), rgba(34, 211, 238, 0.05))',
          border: '1px solid rgba(139, 92, 246, 0.3)'
        }}
      >
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-purple-500/30 blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-cyan-500/30 blur-3xl" />
        </div>
        
        <div className="relative z-10">
          <h1 className="text-3xl md:text-4xl font-bold mb-2"
            style={{ 
              background: 'linear-gradient(90deg, #22D3EE, #60A5FA, #A78BFA)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            {data.name}
          </h1>
          <p className="text-white/60 text-lg mb-6">Complete Astrological Profile</p>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-white/50 text-xs mb-1">Date of Birth</p>
              <p className="text-white font-semibold">{birth_particulars?.date}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-white/50 text-xs mb-1">Time of Birth</p>
              <p className="text-white font-semibold">{birth_particulars?.time}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-white/50 text-xs mb-1">Place</p>
              <p className="text-white font-semibold text-sm">{birth_particulars?.place}</p>
            </div>
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
              <p className="text-white/50 text-xs mb-1">Weekday</p>
              <p className="text-white font-semibold">{birth_particulars?.weekday}</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Lagna & Key Data */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        <SectionCard title="Lagna (Ascendant)" icon={Compass}>
          <div className="text-center">
            <p className="text-4xl font-bold text-cyan-400 mb-2">
              {astronomical_data?.lagna?.split(' ')[0]}
            </p>
            <p className="text-white/60">{astronomical_data?.lagna}</p>
            <p className="text-sm text-purple-400 mt-2">Lord: {astronomical_data?.lagna_lord}</p>
          </div>
        </SectionCard>

        <SectionCard title="Ayanamsa" icon={Star}>
          <div className="text-center">
            <p className="text-3xl font-bold text-purple-400 mb-2">
              {astronomical_data?.ayanamsa}
            </p>
            <p className="text-white/60 text-sm">Lahiri Ayanamsa</p>
          </div>
        </SectionCard>

        <SectionCard title="Western Sign" icon={Sun}>
          <div className="text-center">
            <p className="text-3xl font-bold text-amber-400 mb-2">
              {astronomical_data?.western_sunsign}
            </p>
            <p className="text-white/60 text-sm">Tropical Sun Sign</p>
          </div>
        </SectionCard>
      </div>

      {/* Panchang */}
      <SectionCard title="Panchang Details" icon={Calendar}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/20">
            <p className="text-amber-400/70 text-xs mb-1">Tithi</p>
            <p className="text-amber-400 font-bold">{panchang?.tithi?.full_name}</p>
          </div>
          <div className="bg-gradient-to-br from-purple-500/10 to-violet-500/10 rounded-xl p-4 border border-purple-500/20">
            <p className="text-purple-400/70 text-xs mb-1">Nakshatra</p>
            <p className="text-purple-400 font-bold">{panchang?.nakshatra?.name}</p>
            <p className="text-purple-300/60 text-xs">Pada {panchang?.nakshatra?.pada}</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-xl p-4 border border-cyan-500/20">
            <p className="text-cyan-400/70 text-xs mb-1">Yoga</p>
            <p className="text-cyan-400 font-bold">{panchang?.yoga?.name}</p>
          </div>
          <div className="bg-gradient-to-br from-rose-500/10 to-pink-500/10 rounded-xl p-4 border border-rose-500/20">
            <p className="text-rose-400/70 text-xs mb-1">Karana</p>
            <p className="text-rose-400 font-bold">{panchang?.karana?.name}</p>
          </div>
        </div>
      </SectionCard>

      {/* Hindu Calendar */}
      <SectionCard title="Hindu Calendar" icon={BookOpen}>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <p className="text-white/50">Vikram Samvat</p>
            <p className="text-white font-semibold">{hindu_calendar?.vikram_samvat}</p>
          </div>
          <div>
            <p className="text-white/50">Lunar Month</p>
            <p className="text-white font-semibold">{hindu_calendar?.lunar_month}</p>
          </div>
          <div>
            <p className="text-white/50">Paksha</p>
            <p className="text-white font-semibold">{hindu_calendar?.paksha}</p>
          </div>
          <div>
            <p className="text-white/50">Season</p>
            <p className="text-white font-semibold">{hindu_calendar?.season}</p>
          </div>
        </div>
      </SectionCard>
    </div>
  );
};

// Planets Tab
const PlanetsTab = ({ data }) => {
  if (!data?.planets) return null;
  
  const planets = Object.entries(data.planets);
  
  return (
    <div className="space-y-6">
      <SectionCard title="Planetary Positions (D1 - Rashi Chart)" icon={CircleDot}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-2 text-white/50 font-medium">Planet</th>
                <th className="text-left py-3 px-2 text-white/50 font-medium">Sign</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">Degree</th>
                <th className="text-left py-3 px-2 text-white/50 font-medium">Nakshatra</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">Pada</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">House</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">Retro</th>
              </tr>
            </thead>
            <tbody>
              {planets.map(([planet, info]) => {
                const style = getPlanetStyle(planet);
                return (
                  <tr key={planet} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className={`py-3 px-2 font-semibold ${style.text}`}>{planet}</td>
                    <td className="py-3 px-2 text-white">{info.sign}</td>
                    <td className="py-3 px-2 text-center text-white/80 font-mono">{info.degree?.toFixed(2)}°</td>
                    <td className="py-3 px-2 text-purple-300">{info.nakshatra}</td>
                    <td className="py-3 px-2 text-center text-purple-400">{info.nakshatra_pada}</td>
                    <td className="py-3 px-2 text-center text-cyan-400 font-semibold">H{info.house}</td>
                    <td className="py-3 px-2 text-center">
                      {info.retrograde ? (
                        <span className="text-red-400 font-bold">R</span>
                      ) : (
                        <span className="text-white/30">-</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Planet Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {planets.map(([planet, info]) => {
          const style = getPlanetStyle(planet);
          return (
            <motion.div
              key={planet}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              whileHover={{ scale: 1.02 }}
              className={`rounded-xl p-5 border ${style.border}`}
              style={{ background: `linear-gradient(135deg, ${style.bg.split(' ')[0].replace('from-', '')}, ${style.bg.split(' ')[1].replace('to-', '')})`.replace(/\//g, '') }}
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className={`text-xl font-bold ${style.text}`}>{planet}</h4>
                {info.retrograde && <span className="text-red-400 text-sm font-bold">Retrograde</span>}
              </div>
              <div className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-white/50">Sign</span>
                  <span className="text-white font-medium">{info.sign}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Degree</span>
                  <span className="text-white font-mono">{info.degree?.toFixed(2)}°</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Nakshatra</span>
                  <span className="text-purple-300">{info.nakshatra} - {info.nakshatra_pada}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">House</span>
                  <span className="text-cyan-400 font-semibold">House {info.house}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-white/50">Lord</span>
                  <span className="text-amber-400">{info.nakshatra_lord}</span>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// Vargas Tab (Divisional Charts)
const VargasTab = ({ data }) => {
  const [selectedVarga, setSelectedVarga] = useState('D1_Rashi');
  
  if (!data?.divisional_charts) return null;
  
  const vargaList = [
    { key: 'D1_Rashi', name: 'Rashi', division: 1, display: 'D1' },
    { key: 'D2_Hora', name: 'Hora', division: 2, display: 'D2' },
    { key: 'D3_Drekkana', name: 'Drekkana', division: 3, display: 'D3' },
    { key: 'D4_Chaturthamsa', name: 'Chaturthamsa', division: 4, display: 'D4' },
    { key: 'D7_Saptamsa', name: 'Saptamsa', division: 7, display: 'D7' },
    { key: 'D9_Navamsa', name: 'Navamsa', division: 9, display: 'D9' },
    { key: 'D10_Dasamsa', name: 'Dasamsa', division: 10, display: 'D10' },
    { key: 'D12_Dwadasamsa', name: 'Dwadasamsa', division: 12, display: 'D12' },
    { key: 'D16_Shodasamsa', name: 'Shodasamsa', division: 16, display: 'D16' },
    { key: 'D20_Vimsamsa', name: 'Vimsamsa', division: 20, display: 'D20' },
    { key: 'D24_Chaturvimsamsa', name: 'Chaturvimsamsa', division: 24, display: 'D24' },
    { key: 'D27_Saptavimsamsa', name: 'Saptavimsamsa', division: 27, display: 'D27' },
    { key: 'D30_Trimsamsa', name: 'Trimsamsa', division: 30, display: 'D30' },
    { key: 'D40_Khavedamsa', name: 'Khavedamsa', division: 40, display: 'D40' },
    { key: 'D45_Akshavedamsa', name: 'Akshavedamsa', division: 45, display: 'D45' },
    { key: 'D60_Shashtiamsa', name: 'Shashtiamsa', division: 60, display: 'D60' },
  ];
  
  const currentVarga = data.divisional_charts[selectedVarga];
  
  return (
    <div className="space-y-6">
      {/* Varga Selector */}
      <div className="flex flex-wrap gap-2">
        {vargaList.map((varga) => (
          <button
            key={varga.key}
            onClick={() => setSelectedVarga(varga.key)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedVarga === varga.key
                ? 'bg-gradient-to-r from-cyan-500 to-purple-500 text-white shadow-lg shadow-purple-500/25'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
            }`}
          >
            {varga.display}
          </button>
        ))}
      </div>

      {/* Selected Varga Details */}
      <SectionCard 
        title={`${vargaList.find(v => v.key === selectedVarga)?.display || ''} - ${vargaList.find(v => v.key === selectedVarga)?.name || ''} Chart`} 
        icon={Grid3X3}
      >
        {currentVarga ? (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-2 text-white/50 font-medium">Planet</th>
                  <th className="text-left py-3 px-2 text-white/50 font-medium">Sign</th>
                  <th className="text-center py-3 px-2 text-white/50 font-medium">Sign Position</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(currentVarga).filter(([key]) => !key.startsWith('_')).map(([planet, info]) => {
                  const style = getPlanetStyle(planet);
                  const degreeInSign = info?.longitude ? (info.longitude % 30).toFixed(2) : 'N/A';
                  return (
                    <tr key={planet} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                      <td className={`py-3 px-2 font-semibold ${style.text}`}>{planet}</td>
                      <td className="py-3 px-2 text-white">{info?.sign || 'N/A'}</td>
                      <td className="py-3 px-2 text-center text-cyan-400 font-mono">{degreeInSign}°</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-white/50 text-center py-8">No data available for {vargaList.find(v => v.key === selectedVarga)?.display}</p>
        )}
      </SectionCard>
    </div>
  );
};

// Dasha Tab with expandable Antardashas
const DashaTab = ({ data }) => {
  const [selectedSystem, setSelectedSystem] = useState('vimshottari');
  const [expandedMD, setExpandedMD] = useState(null);
  
  if (!data?.all_dashas) return null;
  
  const dashaSystemsList = [
    { key: 'vimshottari', name: 'Vimshottari', years: 120 },
    { key: 'yogini', name: 'Yogini', years: 36 },
    { key: 'kalachakra', name: 'Kalachakra', years: 100 },
    { key: 'chara_dasha', name: 'Chara', years: 'Variable' },
    { key: 'narayana_dasha', name: 'Narayana', years: 'Variable' },
    { key: 'ashtottari', name: 'Ashtottari', years: 108 },
    { key: 'shodashottari', name: 'Shodashottari', years: 116 },
    { key: 'dwadashottari', name: 'Dwadashottari', years: 112 },
    { key: 'tribhagi40', name: 'Tribhagi-40', years: 40 },
    { key: 'shattrimshatsama', name: 'Shattrimshatsama', years: 36 },
    { key: 'sthira_dasha', name: 'Sthira', years: 'Variable' },
    { key: 'shoola_dasha', name: 'Shoola', years: 'Variable' },
  ];
  
  const currentDasha = data.all_dashas[selectedSystem];
  
  const toggleMD = (idx) => {
    setExpandedMD(expandedMD === idx ? null : idx);
  };
  
  return (
    <div className="space-y-6">
      {/* Dasha System Selector */}
      <div className="flex flex-wrap gap-2">
        {dashaSystemsList.map((system) => {
          const hasData = data.all_dashas[system.key];
          return (
            <button
              key={system.key}
              onClick={() => { setSelectedSystem(system.key); setExpandedMD(null); }}
              disabled={!hasData}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                selectedSystem === system.key
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg shadow-purple-500/25'
                  : hasData
                    ? 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
                    : 'bg-white/5 text-white/30 border border-white/5 cursor-not-allowed'
              }`}
            >
              {system.name}
            </button>
          );
        })}
      </div>

      {/* Dasha Info Card */}
      {currentDasha && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-gradient-to-br from-purple-500/10 to-violet-500/10 rounded-xl p-3 border border-purple-500/20">
            <p className="text-purple-400/70 text-xs mb-1">Total Cycle</p>
            <p className="text-purple-400 font-bold">{currentDasha.total_cycle || dashaSystemsList.find(s => s.key === selectedSystem)?.years} years</p>
          </div>
          <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-xl p-3 border border-cyan-500/20">
            <p className="text-cyan-400/70 text-xs mb-1">Balance at Birth</p>
            <p className="text-cyan-400 font-bold text-sm">
              {currentDasha.balance?.formatted || 
               (typeof currentDasha.balance === 'number' ? currentDasha.balance.toFixed(4) + ' yrs' : 'N/A')}
            </p>
          </div>
          <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-3 border border-amber-500/20">
            <p className="text-amber-400/70 text-xs mb-1">Starting Dasha</p>
            <p className="text-amber-400 font-bold">{currentDasha.balance?.lord || currentDasha.mahadashas?.[0]?.lord || 'N/A'}</p>
          </div>
          <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-xl p-3 border border-pink-500/20">
            <p className="text-pink-400/70 text-xs mb-1">Dasha at Birth</p>
            <p className="text-pink-400 font-bold text-xs">{currentDasha.dasha_at_birth || 'N/A'}</p>
          </div>
        </div>
      )}

      {/* Current Dasha Display with Antardashas */}
      <SectionCard title={`${dashaSystemsList.find(s => s.key === selectedSystem)?.name || ''} Mahadashas`} icon={Clock}>
        {currentDasha?.mahadashas ? (
          <div className="space-y-2">
            {currentDasha.mahadashas.map((md, idx) => {
              const mdName = md.lord || md.planet || md.sign || md.rasi;
              const style = getPlanetStyle(mdName);
              const duration = md.years || md.duration_years || md.duration;
              const hasAntardashas = md.antardashas && md.antardashas.length > 0;
              const isExpanded = expandedMD === idx;
              
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.03 }}
                  className={`rounded-xl border ${style.border} overflow-hidden`}
                  style={{ background: 'rgba(255,255,255,0.02)' }}
                >
                  {/* Mahadasha Header */}
                  <div 
                    className={`p-4 flex items-center justify-between cursor-pointer hover:bg-white/5 transition-colors ${hasAntardashas ? 'cursor-pointer' : ''}`}
                    onClick={() => hasAntardashas && toggleMD(idx)}
                  >
                    <div className="flex items-center gap-3">
                      {hasAntardashas && (
                        <ChevronRight className={`w-5 h-5 text-white/50 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      )}
                      <div>
                        <h4 className={`text-lg font-bold ${style.text}`}>
                          {mdName} Mahadasha
                        </h4>
                        <p className="text-white/50 text-sm">
                          {md.start_date} → {md.end_date}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-cyan-400 font-semibold text-lg">
                        {typeof duration === 'number' ? duration.toFixed(2) : duration || 'N/A'} yrs
                      </p>
                      {hasAntardashas && (
                        <p className="text-white/40 text-xs">{md.antardashas.length} Antardashas</p>
                      )}
                    </div>
                  </div>
                  
                  {/* Antardashas (Subperiods) */}
                  <AnimatePresence>
                    {isExpanded && hasAntardashas && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="border-t border-white/10 bg-white/5"
                      >
                        <div className="p-4">
                          <h5 className="text-sm font-semibold text-white/60 mb-3">
                            Antardashas (Sub-periods) in {mdName} Mahadasha
                          </h5>
                          <div className="space-y-2">
                            {md.antardashas.map((ad, adIdx) => {
                              const adName = ad.lord || ad.planet || ad.sign;
                              const adStyle = getPlanetStyle(adName);
                              const adDuration = ad.years || ad.duration;
                              const hasPDs = ad.pratyantardashas && ad.pratyantardashas.length > 0;
                              
                              return (
                                <AntardashaCard 
                                  key={adIdx}
                                  ad={ad}
                                  adName={adName}
                                  adStyle={adStyle}
                                  adDuration={adDuration}
                                  hasPDs={hasPDs}
                                  mdName={mdName}
                                />
                              );
                            })}
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <p className="text-white/50 text-center py-8">No data available</p>
        )}
      </SectionCard>
    </div>
  );
};

// Antardasha Card Component with expandable Pratyantardashas
const AntardashaCard = ({ ad, adName, adStyle, adDuration, hasPDs, mdName }) => {
  const [expanded, setExpanded] = useState(false);
  
  return (
    <div className={`rounded-lg border ${adStyle.border} bg-white/5 overflow-hidden`}>
      <div 
        className={`p-3 flex justify-between items-center ${hasPDs ? 'cursor-pointer hover:bg-white/5' : ''}`}
        onClick={() => hasPDs && setExpanded(!expanded)}
      >
        <div className="flex items-center gap-2">
          {hasPDs && (
            <ChevronRight className={`w-4 h-4 text-white/40 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          )}
          <span className={`font-semibold ${adStyle.text}`}>{adName}</span>
        </div>
        <div className="text-right">
          {adDuration && (
            <span className="text-cyan-400 text-sm font-mono">
              {typeof adDuration === 'number' ? adDuration.toFixed(3) : adDuration} yrs
            </span>
          )}
          <p className="text-white/40 text-xs">{ad.start_date} → {ad.end_date}</p>
          {hasPDs && <p className="text-white/30 text-xs">{ad.pratyantardashas.length} PDs</p>}
        </div>
      </div>
      
      {/* Pratyantardashas */}
      <AnimatePresence>
        {expanded && hasPDs && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10 bg-black/20 p-3"
          >
            <p className="text-xs text-purple-400/70 mb-2">
              Pratyantardashas in {mdName}-{adName}
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-1 text-xs">
              {ad.pratyantardashas.map((pd, pdIdx) => {
                const pdStyle = getPlanetStyle(pd.lord);
                return (
                  <PratyantardashaCard key={pdIdx} pd={pd} pdStyle={pdStyle} adName={adName} mdName={mdName} />
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Pratyantardasha Card with expandable Sookshmas
const PratyantardashaCard = ({ pd, pdStyle, adName, mdName }) => {
  const [expanded, setExpanded] = useState(false);
  const hasSookshmas = pd.sookshmas && pd.sookshmas.length > 0;
  
  return (
    <div className={`rounded border ${pdStyle.border} bg-white/5 overflow-hidden`}>
      <div 
        className={`p-2 ${hasSookshmas ? 'cursor-pointer hover:bg-white/10' : ''}`}
        onClick={() => hasSookshmas && setExpanded(!expanded)}
      >
        <div className="flex justify-between items-center">
          <div className="flex items-center gap-1">
            {hasSookshmas && (
              <ChevronRight className={`w-3 h-3 text-white/30 transition-transform ${expanded ? 'rotate-90' : ''}`} />
            )}
            <span className={`font-medium ${pdStyle.text}`}>{pd.lord}</span>
          </div>
          <span className="text-cyan-400 font-mono">{pd.days?.toFixed(1)}d</span>
        </div>
        <p className="text-white/30 text-[10px] mt-1">{pd.start_date} → {pd.end_date}</p>
      </div>
      
      {/* Sookshmas */}
      <AnimatePresence>
        {expanded && hasSookshmas && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10 bg-black/30 p-2"
          >
            <p className="text-[10px] text-amber-400/70 mb-1">Sookshmas</p>
            <div className="grid grid-cols-3 gap-1">
              {pd.sookshmas.map((sd, sdIdx) => {
                const sdStyle = getPlanetStyle(sd.lord);
                return (
                  <SookshmaCard key={sdIdx} sd={sd} sdStyle={sdStyle} />
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Sookshma Card with expandable Pranas
const SookshmaCard = ({ sd, sdStyle }) => {
  const [expanded, setExpanded] = useState(false);
  const hasPranas = sd.pranas && sd.pranas.length > 0;
  
  return (
    <div className={`rounded border ${sdStyle.border} bg-white/5 overflow-hidden`}>
      <div 
        className={`p-1 ${hasPranas ? 'cursor-pointer hover:bg-white/10' : ''}`}
        onClick={() => hasPranas && setExpanded(!expanded)}
      >
        <div className="flex justify-between items-center">
          <span className={`text-[10px] font-medium ${sdStyle.text}`}>{sd.lord}</span>
          <span className="text-cyan-400 text-[10px] font-mono">{sd.hours?.toFixed(1)}h</span>
        </div>
      </div>
      
      {/* Pranas */}
      <AnimatePresence>
        {expanded && hasPranas && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-white/10 bg-black/40 p-1"
          >
            <p className="text-[8px] text-rose-400/70 mb-1">Pranas</p>
            <div className="flex flex-wrap gap-1">
              {sd.pranas.map((pr, prIdx) => {
                const prStyle = getPlanetStyle(pr.lord);
                return (
                  <span 
                    key={prIdx} 
                    className={`text-[8px] px-1 rounded ${prStyle.text} bg-white/5`}
                    title={`${pr.minutes?.toFixed(1)} min`}
                  >
                    {pr.lord.substring(0, 2)}: {pr.minutes?.toFixed(0)}m
                  </span>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// Varshaphala Tab
const VarshaphalaTab = ({ data }) => {
  const [selectedYear, setSelectedYear] = useState(0);
  
  // Varshaphala is a list of years directly
  if (!data?.varshaphala || !Array.isArray(data.varshaphala) || data.varshaphala.length === 0) {
    return (
      <SectionCard title="Varshaphala (Annual Charts)" icon={Calendar}>
        <p className="text-white/50 text-center py-8">No Varshaphala data available</p>
      </SectionCard>
    );
  }
  
  const years = data.varshaphala;
  const currentYear = years[selectedYear];
  const birthYear = data.birth_particulars?.date ? parseInt(data.birth_particulars.date.split(' ')[2]) : 1997;
  
  return (
    <div className="space-y-6">
      {/* Year Selector */}
      <div className="flex flex-wrap gap-2">
        {years.map((year, idx) => (
          <button
            key={idx}
            onClick={() => setSelectedYear(idx)}
            className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
              selectedYear === idx
                ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg shadow-amber-500/25'
                : 'bg-white/5 text-white/70 hover:bg-white/10 border border-white/10'
            }`}
          >
            Y{year.year_completed}
          </button>
        ))}
      </div>

      {/* Current Year Details */}
      {currentYear && (
        <>
          {/* Year Overview Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gradient-to-br from-amber-500/10 to-orange-500/10 rounded-xl p-4 border border-amber-500/20">
              <p className="text-amber-400/70 text-xs mb-1">Year Completed</p>
              <p className="text-amber-400 font-bold text-2xl">{currentYear.year_completed}</p>
              <p className="text-white/40 text-xs">Age: {currentYear.age}</p>
            </div>
            <div className="bg-gradient-to-br from-purple-500/10 to-violet-500/10 rounded-xl p-4 border border-purple-500/20">
              <p className="text-purple-400/70 text-xs mb-1">Year Lord</p>
              <p className="text-purple-400 font-bold text-xl">{currentYear.year_lord}</p>
              <p className="text-white/40 text-xs">{currentYear.year_lord_abbr}</p>
            </div>
            <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 rounded-xl p-4 border border-cyan-500/20">
              <p className="text-cyan-400/70 text-xs mb-1">Solar Return</p>
              <p className="text-cyan-400 font-bold text-sm">{currentYear.solar_return_date}</p>
              <p className="text-white/40 text-xs">{currentYear.solar_return_time}</p>
            </div>
            <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 rounded-xl p-4 border border-pink-500/20">
              <p className="text-pink-400/70 text-xs mb-1">Varsha Lagna</p>
              <p className="text-pink-400 font-bold">{currentYear.varsha_lagna?.sign}</p>
              <p className="text-white/40 text-xs">{currentYear.varsha_lagna?.degree}</p>
            </div>
          </div>

          {/* Muntha Details */}
          <SectionCard title="Muntha Details" icon={Compass}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <p className="text-white/50 text-xs mb-1">Muntha Sign</p>
                <p className="text-pink-400 font-bold text-xl">{currentYear.muntha?.sign}</p>
                <p className="text-white/40 text-xs">{currentYear.muntha?.degree}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <p className="text-white/50 text-xs mb-1">House from Lagna</p>
                <p className="text-cyan-400 font-bold text-xl">H{currentYear.muntha?.house_from_lagna || '-'}</p>
              </div>
              <div className="bg-white/5 rounded-lg p-4 border border-white/10">
                <p className="text-white/50 text-xs mb-1">Munthesha (Lord)</p>
                <p className="text-amber-400 font-bold text-xl">{currentYear.muntha?.munthesha || '-'}</p>
              </div>
            </div>
          </SectionCard>

          {/* Planetary Positions for this Year */}
          {currentYear.planets && (
            <SectionCard title={`Planetary Positions (Year ${currentYear.year_completed})`} icon={CircleDot}>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-white/10">
                      <th className="text-left py-3 px-2 text-white/50 font-medium">Planet</th>
                      <th className="text-left py-3 px-2 text-white/50 font-medium">Sign</th>
                      <th className="text-center py-3 px-2 text-white/50 font-medium">Degree</th>
                      <th className="text-center py-3 px-2 text-white/50 font-medium">House</th>
                      <th className="text-center py-3 px-2 text-white/50 font-medium">Retro</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(currentYear.planets).map(([planet, info]) => {
                      const style = getPlanetStyle(planet);
                      return (
                        <tr key={planet} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                          <td className={`py-3 px-2 font-semibold ${style.text}`}>{planet}</td>
                          <td className="py-3 px-2 text-white">{info?.sign}</td>
                          <td className="py-3 px-2 text-center text-white/80 font-mono">{info?.degree}</td>
                          <td className="py-3 px-2 text-center text-cyan-400 font-semibold">H{info?.house || '-'}</td>
                          <td className="py-3 px-2 text-center">
                            {info?.retrograde ? (
                              <span className="text-red-400 font-bold">R</span>
                            ) : (
                              <span className="text-white/30">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
};

// Shadbala Tab
const ShadbalaTab = ({ data }) => {
  if (!data?.shadbala) return null;
  
  const planets = Object.entries(data.shadbala).filter(([key]) => 
    ['Sun', 'Moon', 'Mars', 'Mercury', 'Jupiter', 'Venus', 'Saturn'].includes(key)
  );
  
  return (
    <div className="space-y-6">
      <SectionCard title="Shadbala (Six-Fold Strength)" icon={BarChart3}>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-2 text-white/50 font-medium">Planet</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">Total (Sha.)</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">Rupas</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">Min Req.</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">% of Req.</th>
                <th className="text-center py-3 px-2 text-white/50 font-medium">Rank</th>
              </tr>
            </thead>
            <tbody>
              {planets.map(([planet, info]) => {
                const style = getPlanetStyle(planet);
                const percentage = (info.percentage_required || 0) * 100;
                return (
                  <tr key={planet} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className={`py-3 px-2 font-semibold ${style.text}`}>{planet}</td>
                    <td className="py-3 px-2 text-center text-white font-mono">
                      {info.total_shadbala?.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-center text-cyan-400 font-semibold">
                      {info.shadbala_rupas?.toFixed(2)}
                    </td>
                    <td className="py-3 px-2 text-center text-white/70">
                      {info.min_requirement}
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`font-semibold ${
                        percentage >= 100 ? 'text-green-400' :
                        percentage >= 75 ? 'text-yellow-400' : 'text-red-400'
                      }`}>
                        {percentage.toFixed(1)}%
                      </span>
                    </td>
                    <td className="py-3 px-2 text-center">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        info.relative_rank <= 2 ? 'bg-green-500/20 text-green-400' :
                        info.relative_rank <= 4 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'
                      }`}>
                        #{info.relative_rank}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SectionCard>

      {/* Component Breakdown */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {planets.map(([planet, info]) => {
          const style = getPlanetStyle(planet);
          const percentage = Math.min((info.percentage_required || 0) * 100, 200);
          return (
            <motion.div
              key={planet}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white/5 rounded-xl p-4 border border-white/10"
            >
              <div className="flex justify-between mb-3">
                <span className={`font-bold text-lg ${style.text}`}>{planet}</span>
                <span className="text-amber-400 font-semibold">#{info.relative_rank}</span>
              </div>
              
              {/* Progress bar */}
              <div className="h-3 bg-white/10 rounded-full overflow-hidden mb-3">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${(percentage / 200) * 100}%` }}
                  transition={{ duration: 1, ease: 'easeOut' }}
                  className={`h-full rounded-full ${
                    percentage >= 100 ? 'bg-gradient-to-r from-green-500 to-emerald-400' :
                    percentage >= 75 ? 'bg-gradient-to-r from-yellow-500 to-amber-400' :
                    'bg-gradient-to-r from-red-500 to-rose-400'
                  }`}
                />
              </div>
              
              {/* Component values */}
              <div className="grid grid-cols-3 gap-2 text-xs">
                <div className="bg-purple-500/10 rounded p-2 text-center">
                  <p className="text-purple-300/70">Sthana</p>
                  <p className="text-purple-400 font-semibold">{info.sthana_bala?.toFixed(1)}</p>
                </div>
                <div className="bg-blue-500/10 rounded p-2 text-center">
                  <p className="text-blue-300/70">Dig</p>
                  <p className="text-blue-400 font-semibold">{info.dig_bala?.toFixed(1)}</p>
                </div>
                <div className="bg-green-500/10 rounded p-2 text-center">
                  <p className="text-green-300/70">Kaala</p>
                  <p className="text-green-400 font-semibold">{info.kaala_bala?.toFixed(1)}</p>
                </div>
                <div className="bg-yellow-500/10 rounded p-2 text-center">
                  <p className="text-yellow-300/70">Chesta</p>
                  <p className="text-yellow-400 font-semibold">{info.chesta_bala?.toFixed(1)}</p>
                </div>
                <div className="bg-orange-500/10 rounded p-2 text-center">
                  <p className="text-orange-300/70">Naisargika</p>
                  <p className="text-orange-400 font-semibold">{info.naisargika_bala?.toFixed(1)}</p>
                </div>
                <div className="bg-red-500/10 rounded p-2 text-center">
                  <p className="text-red-300/70">Drig</p>
                  <p className="text-red-400 font-semibold">{info.drig_bala?.toFixed(1)}</p>
                </div>
              </div>
              
              {/* Ishta/Kashta */}
              <div className="mt-3 flex justify-between text-xs">
                <span className="text-green-400">Ishta: {info.ishta_phala?.toFixed(1)}</span>
                <span className="text-red-400">Kashta: {info.kashta_phala?.toFixed(1)}</span>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
};

// Ashtakavarga Tab
const AshtakavargaTab = ({ data }) => {
  if (!data?.ashtakavarga) return null;
  
  const { bhinashtakavarga, sarvashtakavarga } = data.ashtakavarga;
  const signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 
                 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'];
  
  return (
    <div className="space-y-6">
      {/* SAV Summary */}
      {sarvashtakavarga && (
        <SectionCard title="Sarvashtakavarga (SAV)" icon={Layers}>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
            {sarvashtakavarga.sign_totals?.map((bindus, idx) => (
              <div 
                key={idx}
                className="bg-gradient-to-br from-cyan-500/10 to-purple-500/10 rounded-xl p-3 border border-cyan-500/20 text-center"
              >
                <p className="text-white/60 text-xs mb-1">{signs[idx]}</p>
                <p className="text-cyan-400 text-2xl font-bold">{bindus}</p>
              </div>
            ))}
          </div>
          <div className="mt-4 p-4 bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl border border-amber-500/20">
            <p className="text-center">
              <span className="text-white/60">Total SAV: </span>
              <span className="text-amber-400 text-3xl font-bold">{sarvashtakavarga.grand_total}</span>
              <span className="text-white/40 ml-2">(Expected: 337)</span>
            </p>
          </div>
        </SectionCard>
      )}

      {/* BAV by Planet */}
      {bhinashtakavarga && (
        <SectionCard title="Bhinnashtakavarga (BAV)" icon={Grid3X3}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10">
                  <th className="text-left py-3 px-2 text-white/50 font-medium sticky left-0 bg-slate-900/80">Sign</th>
                  {Object.keys(bhinashtakavarga).filter(k => k !== 'Asc' && k !== 'Lagna').slice(0, 7).map(planet => (
                    <th key={planet} className={`text-center py-3 px-2 font-medium ${getPlanetStyle(planet).text}`}>
                      {planet.substring(0, 3)}
                    </th>
                  ))}
                  <th className="text-center py-3 px-2 text-amber-400 font-medium">SAV</th>
                </tr>
              </thead>
              <tbody>
                {signs.map((sign, idx) => (
                  <tr key={sign} className="border-b border-white/5 hover:bg-white/5">
                    <td className="py-2 px-2 text-cyan-300 font-medium sticky left-0 bg-slate-900/80">{sign}</td>
                    {Object.entries(bhinashtakavarga).filter(([k]) => k !== 'Asc' && k !== 'Lagna').slice(0, 7).map(([planet, planetData]) => (
                      <td key={planet} className="py-2 px-2 text-center text-yellow-400 font-semibold">
                        {planetData.sign_totals?.[idx] || 0}
                      </td>
                    ))}
                    <td className="py-2 px-2 text-center text-amber-400 font-bold">
                      {sarvashtakavarga?.sign_totals?.[idx] || 0}
                    </td>
                  </tr>
                ))}
                {/* Totals row */}
                <tr className="border-t-2 border-purple-500/30 bg-purple-500/10">
                  <td className="py-3 px-2 text-purple-300 font-bold sticky left-0 bg-purple-900/50">Total</td>
                  {Object.entries(bhinashtakavarga).filter(([k]) => k !== 'Asc' && k !== 'Lagna').slice(0, 7).map(([planet, planetData]) => (
                    <td key={planet} className="py-3 px-2 text-center text-purple-400 font-bold">
                      {planetData.grand_total || 0}
                    </td>
                  ))}
                  <td className="py-3 px-2 text-center text-amber-400 font-bold text-lg">
                    {sarvashtakavarga?.grand_total || 0}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </SectionCard>
      )}
    </div>
  );
};

// Yogas Tab
const YogasTab = ({ data }) => {
  if (!data?.yogas) return null;
  
  const yogas = Array.isArray(data.yogas) ? data.yogas : [];
  
  return (
    <div className="space-y-6">
      <SectionCard title="Yogas (Planetary Combinations)" icon={Sparkles}>
        {yogas.length > 0 ? (
          <div className="space-y-4">
            {yogas.map((yoga, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.05 }}
                className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-xl p-4 border border-purple-500/20"
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="text-lg font-bold text-purple-400 mb-1">{yoga.name}</h4>
                    <p className="text-white/60 text-sm">{yoga.description}</p>
                  </div>
                  {yoga.strength && (
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      yoga.strength === 'Strong' ? 'bg-green-500/20 text-green-400' :
                      yoga.strength === 'Medium' ? 'bg-yellow-500/20 text-yellow-400' :
                      'bg-red-500/20 text-red-400'
                    }`}>
                      {yoga.strength}
                    </span>
                  )}
                </div>
                {yoga.planets && yoga.planets.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    {yoga.planets.map((planet, pidx) => {
                      const style = getPlanetStyle(planet);
                      return (
                        <span 
                          key={pidx}
                          className={`px-2 py-1 rounded text-xs font-medium ${style.text} bg-white/5 border ${style.border}`}
                        >
                          {planet}
                        </span>
                      );
                    })}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <p className="text-white/50 text-center py-8">No yogas detected in this chart</p>
        )}
      </SectionCard>
    </div>
  );
};

// Main Component
const KernelViewer = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchKernel();
  }, []);

  const fetchKernel = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/mandeep-kernel/parashara-complete`);
      if (!response.ok) throw new Error('Failed to fetch kernel data');
      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const downloadKernel = () => {
    if (!data) return;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${data.name?.replace(/\s+/g, '_')}_kernel.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (loading) return <LoadingScreen />;

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#0B0E1A' }}>
        <div className="text-center">
          <p className="text-red-400 text-lg mb-4">Error: {error}</p>
          <button
            onClick={fetchKernel}
            className="px-6 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-xl hover:opacity-90 transition-all flex items-center gap-2 mx-auto"
          >
            <RefreshCw className="w-5 h-5" />
            Retry
          </button>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview': return <OverviewTab data={data} />;
      case 'planets': return <PlanetsTab data={data} />;
      case 'vargas': return <VargasTab data={data} />;
      case 'dasha': return <DashaTab data={data} />;
      case 'varshaphala': return <VarshaphalaTab data={data} />;
      case 'shadbala': return <ShadbalaTab data={data} />;
      case 'ashtakavarga': return <AshtakavargaTab data={data} />;
      case 'yogas': return <YogasTab data={data} />;
      default: return <OverviewTab data={data} />;
    }
  };

  return (
    <div className="min-h-screen" style={{ background: '#0B0E1A' }}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full bg-purple-500/10 blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 rounded-full bg-cyan-500/10 blur-3xl" />
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8"
        >
          <div>
            <h1 
              className="text-3xl md:text-4xl font-bold mb-2"
              style={{ 
                background: 'linear-gradient(90deg, #22D3EE, #60A5FA, #A78BFA, #C084FC)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}
            >
              Astrological Kernel
            </h1>
            <p className="text-white/50">Complete Parashara Analysis</p>
          </div>
          <button
            onClick={downloadKernel}
            className="px-5 py-3 bg-gradient-to-r from-cyan-500 to-purple-500 text-white rounded-xl hover:opacity-90 transition-all flex items-center gap-2 font-medium shadow-lg shadow-purple-500/25"
          >
            <Download className="w-5 h-5" />
            Download JSON
          </button>
        </motion.div>

        {/* Tab Navigation */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="flex overflow-x-auto gap-2 pb-4 mb-6 scrollbar-hide"
        >
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-cyan-500/20 to-purple-500/20 text-white border border-cyan-500/30 shadow-lg'
                    : 'bg-white/5 text-white/60 hover:bg-white/10 border border-transparent'
                }`}
              >
                <Icon className={`w-4 h-4 ${activeTab === tab.id ? 'text-cyan-400' : ''}`} />
                {tab.label}
              </button>
            );
          })}
        </motion.div>

        {/* Tab Content */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.2 }}
          >
            {renderTabContent()}
          </motion.div>
        </AnimatePresence>

        {/* Footer */}
        <div className="mt-12 text-center text-white/30 text-sm">
          <p>Generated: {data?.generated_at}</p>
          <p className="mt-1">Powered by Taraanaya Astrological Engine</p>
        </div>
      </div>
    </div>
  );
};

export default KernelViewer;
